import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Crown, Zap, ArrowRight, X, Loader2, Clock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { BASE_URL } from "../lib/api";

const API_URL = BASE_URL;

/**
 * UpgradeModal - Display subscription upgrade prompts when users hit quota limits
 * 
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Callback to close the modal
 * @param {string} errorType - Type of error: "video_limit_reached" | "duration_limit_exceeded" | "ugc_access_required"
 * @param {object} errorData - Error data from the backend API response
 * @param {object} formData - Optional form data to preserve during addon checkout
 * @param {string} returnPath - Path to return to after addon purchase (default: current path)
 */
export function UpgradeModal({ open, onClose, errorType, errorData, formData, returnPath }) {
  const navigate = useNavigate();
  const [purchasingAddon, setPurchasingAddon] = useState(false);

  const handleUpgrade = () => {
    onClose();
    navigate("/pricing");
  };

  const saveFormDataForCheckout = () => {
    // Save form data to localStorage so it can be restored after payment
    if (formData) {
      const checkoutState = {
        formData,
        returnPath: returnPath || window.location.pathname,
        timestamp: Date.now(),
      };
      localStorage.setItem("addon_checkout_state", JSON.stringify(checkoutState));
    }
  };

  const handleBuyDurationAddon = async (addonKey) => {
    setPurchasingAddon(true);
    const token = localStorage.getItem("auth_token");
    
    // Save form data before redirecting to Mollie
    saveFormDataForCheckout();
    
    try {
      const response = await fetch(`${API_URL}/api/subscription/duration-addon/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          addon_key: addonKey,
          origin_url: window.location.origin,
          return_path: returnPath || window.location.pathname,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to create checkout");
      }

      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (error) {
      console.error("Addon checkout error:", error);
      toast.error(error.message || "Failed to purchase add-on");
      setPurchasingAddon(false);
    }
  };

  const handleBuyUgcAddon = async () => {
    setPurchasingAddon(true);
    const token = localStorage.getItem("auth_token");
    
    // Save form data before redirecting to Mollie
    saveFormDataForCheckout();
    
    try {
      const response = await fetch(`${API_URL}/api/subscription/ugc-addon/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          origin_url: window.location.origin,
          return_path: returnPath || window.location.pathname,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to create checkout");
      }

      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (error) {
      console.error("UGC addon checkout error:", error);
      toast.error(error.message || "Failed to purchase add-on");
      setPurchasingAddon(false);
    }
  };

  const renderContent = () => {
    switch (errorType) {
      case "video_limit_reached":
        return (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <DialogTitle className="text-white text-xl">Video Limit Reached</DialogTitle>
                  <DialogDescription className="text-slate-400">
                    You've used all your videos for this period
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                <div className="flex justify-between items-center">
                  <span className="text-slate-300">Videos Used</span>
                  <span className="text-white font-bold">
                    {errorData?.videos_used || 0} / {errorData?.video_limit || 0}
                  </span>
                </div>
                <div className="mt-2 h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-amber-500 rounded-full"
                    style={{ width: "100%" }}
                  />
                </div>
              </div>

              <p className="text-slate-400 text-sm">
                {errorData?.message || "Upgrade your plan to create more videos and unlock additional features."}
              </p>

              {errorData?.upgrade_options && errorData.upgrade_options.length > 0 && (
                <div className="space-y-2">
                  <p className="text-slate-300 text-sm font-medium">Upgrade Options:</p>
                  {errorData.upgrade_options.slice(0, 3).map((option, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-cyan-500/50 transition-colors cursor-pointer"
                      onClick={handleUpgrade}
                    >
                      <div className="flex items-center gap-2">
                        <Crown className="w-4 h-4 text-cyan-400" />
                        <span className="text-white font-medium">{option.name}</span>
                        <Badge variant="outline" className="text-xs text-cyan-400 border-cyan-500/50">
                          {option.video_limit} videos
                        </Badge>
                      </div>
                      <span className="text-cyan-400 font-bold">£{option.monthly_price}/mo</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        );

      case "duration_limit_exceeded":
        return (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-purple-500" />
                </div>
                <div>
                  <DialogTitle className="text-white text-xl">Duration Limit</DialogTitle>
                  <DialogDescription className="text-slate-400">
                    Upgrade for longer videos
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                <div className="flex justify-between items-center">
                  <span className="text-slate-300">Your Max Duration</span>
                  <span className="text-white font-bold">{errorData?.max_duration || 10}s</span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-slate-300">Requested Duration</span>
                  <span className="text-purple-400 font-bold">{errorData?.requested_duration || 0}s</span>
                </div>
              </div>

              <p className="text-slate-400 text-sm">
                {errorData?.message || "Upgrade your plan to create longer videos."}
              </p>

              {errorData?.addon_available && (
                <div className="p-4 bg-gradient-to-r from-purple-500/10 to-cyan-500/10 rounded-lg border border-purple-500/30">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-white font-medium flex items-center gap-2">
                        <Clock className="w-4 h-4 text-purple-400" />
                        Quick Add-on Available
                      </p>
                      <p className="text-slate-400 text-sm">
                        {errorData.addon_available.name} - <span className="text-purple-400 font-bold">£{errorData.addon_available.price}</span> one-time
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleBuyDurationAddon(errorData.addon_available.key)}
                    disabled={purchasingAddon}
                    className="w-full bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-400 hover:to-cyan-400 text-white font-semibold"
                    data-testid="buy-duration-addon-btn"
                  >
                    {purchasingAddon ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Buy Now - £{errorData.addon_available.price}
                      </>
                    )}
                  </Button>
                </div>
              )}

              {errorData?.upgrade_options && errorData.upgrade_options.length > 0 && (
                <div className="space-y-2">
                  <p className="text-slate-300 text-sm font-medium">Or upgrade your plan:</p>
                  {errorData.upgrade_options.slice(0, 2).map((option, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-purple-500/50 transition-colors cursor-pointer"
                      onClick={handleUpgrade}
                    >
                      <div className="flex items-center gap-2">
                        <Crown className="w-4 h-4 text-purple-400" />
                        <span className="text-white font-medium">{option.name}</span>
                        <Badge variant="outline" className="text-xs text-purple-400 border-purple-500/50">
                          {option.max_duration}s max
                        </Badge>
                      </div>
                      <span className="text-purple-400 font-bold">£{option.monthly_price}/mo</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        );

      case "ugc_access_required":
        return (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center">
                  <Crown className="w-6 h-6 text-cyan-500" />
                </div>
                <div>
                  <DialogTitle className="text-white text-xl">Pro Feature</DialogTitle>
                  <DialogDescription className="text-slate-400">
                    UGC Ad Studio requires an upgrade
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <p className="text-slate-400 text-sm">
                {errorData?.message || "UGC Ad Studio is available on Pro and Enterprise plans, or as an add-on to Premium."}
              </p>

              <div className="space-y-3">
                <div className="p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-lg border border-amber-500/30 cursor-pointer hover:border-amber-500/50 transition-colors" onClick={handleUpgrade}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-bold">Pro Plan</p>
                      <p className="text-slate-400 text-xs">Full UGC Ad Studio access</p>
                    </div>
                    <span className="text-amber-400 font-bold text-lg">£69/mo</span>
                  </div>
                </div>

                {errorData?.current_plan === "premium" && (
                  <div className="p-4 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 rounded-lg border border-cyan-500/30">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-white font-medium">UGC Add-on</p>
                        <p className="text-slate-400 text-xs">Add UGC to your Premium plan</p>
                      </div>
                      <span className="text-cyan-400 font-bold">£25/mo</span>
                    </div>
                    <Button
                      onClick={handleBuyUgcAddon}
                      disabled={purchasingAddon}
                      className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white font-semibold"
                      data-testid="buy-ugc-addon-btn"
                    >
                      {purchasingAddon ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4 mr-2" />
                          Add UGC Studio - £25/mo
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {errorData?.current_plan !== "premium" && (
                  <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 cursor-pointer hover:border-cyan-500/50 transition-colors" onClick={handleUpgrade}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">Premium + UGC Add-on</p>
                        <p className="text-slate-400 text-xs">Add UGC to your Premium plan</p>
                      </div>
                      <span className="text-cyan-400 font-bold">£49 + £25/mo</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-slate-800/30 rounded-lg p-3 border border-slate-700/50">
                <p className="text-white text-sm font-medium mb-2">UGC Ad Studio includes:</p>
                <ul className="text-slate-400 text-xs space-y-1">
                  <li>• Subject binding for character consistency</li>
                  <li>• Element binding for product consistency</li>
                  <li>• Multiple product reference images</li>
                  <li>• Regional accent AI voices</li>
                  <li>• Product gallery outros</li>
                </ul>
              </div>
            </div>
          </>
        );

      default:
        return (
          <>
            <DialogHeader>
              <DialogTitle className="text-white">Upgrade Required</DialogTitle>
              <DialogDescription className="text-slate-400">
                {errorData?.message || "Please upgrade your plan to continue."}
              </DialogDescription>
            </DialogHeader>
          </>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-md" data-testid="upgrade-modal">
        {renderContent()}
        <DialogFooter className="flex gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
            data-testid="upgrade-modal-close"
          >
            Maybe Later
          </Button>
          <Button
            onClick={handleUpgrade}
            className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white"
            data-testid="upgrade-modal-upgrade"
          >
            View Plans
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default UpgradeModal;
